import { Button, Form, Input, Skeleton, Space, Typography } from 'antd';
import { useEffect } from 'react';

import {
  ConfirmDeletingElement,
  Error403Unauthorized,
  SetBranchesComponent,
} from '@app/components';
import {
  BRANCHES_LABEL,
  FORBIDDEN_ERROR_MAIN_PART,
  OFF,
  ONE_TIME_CODE,
  PREFIX,
  RULES_IS_REQUIRE,
} from '@app/constants';
import {
  useCreateWarehouse,
  useCreateWarehouseEffects,
} from '@app/pages/Warehouses/WarehouseRegistry/components/CreateWarehouse/hooks';

import type { FC } from 'react';

import '@app/pages/Warehouses/WarehouseRegistry/components/CreateWarehouse/styles/CreateWarehouse.scss';

const { Text, Title } = Typography;
const { TextArea } = Input;

export const CreateWarehouse: FC = () => {
  const {
    form,
    openBranchesModal,
    showBranchesModal,
    hideBranchesModal,
    selectedBranches,
    setSelectedBranches,
    allBranches,
    handleGetBranches,
    handleSaveWarehouse,
    isWarehouseUpdating,
    navigateToRegistry,
    handleGetWarehouse,
    isPageForbidden,
    isPageLoading,
    checkBranchSelection,
    branchError,
    showDeleteButton,
    openDeleteWarehouseModal,
    isOpenDeleteWarehouseModal,
    setIsOpenDeleteWarehouseModal,
    deleteWarehouseHandler,
    editableWarehouse,
  } = useCreateWarehouse();

  useCreateWarehouseEffects({ handleGetBranches, handleGetWarehouse });

  useEffect(() => {
    if (!editableWarehouse || isPageLoading || isPageForbidden) {
      return;
    }

    form.setFieldsValue({
      Name: editableWarehouse.Name || '',
      Comment: editableWarehouse.Comment || '',
    });
  }, [editableWarehouse, form, isPageLoading, isPageForbidden]);

  if (isPageLoading) {
    return <Skeleton active />;
  }

  if (isPageForbidden) {
    return <Error403Unauthorized title={FORBIDDEN_ERROR_MAIN_PART} />;
  }

  return (
    <div className={`${PREFIX}create-warehouse`}>
      <Form
        form={form}
        onFinish={handleSaveWarehouse}
        className="bc-form bc-create-warehouse__form"
        autoComplete={OFF}
      >
        <Space
          direction="vertical"
          size="middle"
          className="bc-create-warehouse__fields"
        >
          <div className="field-container">
            <Text className="label-require" strong>
              Название
            </Text>
            <Form.Item
              name="Name"
              className="mg-0"
              initialValue=""
              rules={RULES_IS_REQUIRE}
            >
              <Input
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={200}
              />
            </Form.Item>
          </div>
          <div className="field-container textarea-container">
            <Text strong>Примечание</Text>
            <Form.Item className="mg-0" name="Comment">
              <TextArea rows={4} maxLength={5000} />
            </Form.Item>
          </div>
          <div className="branches-section">
            <Title level={3} className="mg-0 label-require">
              {BRANCHES_LABEL}
            </Title>

            <SetBranchesComponent
              open={openBranchesModal}
              onCancel={hideBranchesModal}
              selectedBranches={selectedBranches}
              setSelectedBranches={setSelectedBranches}
              allBranches={allBranches ?? []}
              showUserModal={showBranchesModal}
              error={branchError}
              checkBranchSelection={checkBranchSelection}
              required
            />
          </div>

          <div className={`${PREFIX}create-warehouse_buttons`}>
            <Button
              type="primary"
              htmlType="submit"
              className={`${PREFIX}create-warehouse_buttons_item`}
              disabled={isWarehouseUpdating}
              loading={isWarehouseUpdating}
              onClick={() => checkBranchSelection()}
              size="large"
            >
              Сохранить
            </Button>
            <Button
              type="default"
              className={`${PREFIX}create-warehouse_buttons_item`}
              onClick={navigateToRegistry}
              disabled={isWarehouseUpdating}
              size="large"
            >
              Отменить
            </Button>
            {showDeleteButton && (
              <Button
                danger
                className={`${PREFIX}create-warehouse_buttons_item`}
                disabled={isWarehouseUpdating}
                size="large"
                onClick={openDeleteWarehouseModal}
              >
                Удалить
              </Button>
            )}
          </div>
        </Space>
      </Form>
      <ConfirmDeletingElement
        modalOpen={isOpenDeleteWarehouseModal}
        setModalOpen={setIsOpenDeleteWarehouseModal}
        handleDelete={deleteWarehouseHandler}
        content="Вы уверены, что хотите удалить склад?"
        title="Удаление склада"
      />
    </div>
  );
};
